import { Tabs, Tab } from '@heroui/react';
import { twMerge } from 'tailwind-merge';
import { useEffect, useState } from 'react';
import type React from 'react';
import { type InferKeyOrTitle, type Segment, type SegmentedControlProps } from './SegmentedControl.types';

/*

    progressive (default): only the selected tab content is rendered when the SegmentedControl
                            component renders, and the other tab content is rendered progressively over time
    eager: all tab content is rendered immediately when the SegmentedControl component renders
    lazy: only the selected tab content is rendered when the SegmentedControl component renders and other
                            tabs are only rendered when they are selected
*/
type RenderStrategy = 'eager' | 'lazy' | 'progressive';

type Props<Segments extends readonly Segment[]> = SegmentedControlProps<Segments> & {
    renderStrategy?: RenderStrategy;
    progressiveRenderingDelay?: number;
};

function useRenderedSegments<Segments extends readonly Segment[]>(
    segments: Segments,
    selectedSegment: string,
    renderStrategy: RenderStrategy,
) {
    const [renderedSegments, setRenderedSegments] = useState<Set<string>>(() => {
        const segmentKeys = segments.map(({ title, ...rest }) => ('key' in rest ? rest.key : title));

        if (renderStrategy === 'eager') {
            return new Set(segmentKeys);
        }
        if (renderStrategy === 'lazy') {
            return new Set([selectedSegment]);
        }
        // progressive - start with just the selected segment
        return new Set([selectedSegment]);
    });

    // Update rendered segments when selected segment or strategy changes
    useEffect(() => {
        const segmentKeys = segments.map(({ title, ...rest }) => ('key' in rest ? rest.key : title));

        if (renderStrategy === 'eager') {
            setRenderedSegments(new Set(segmentKeys));
        } else if (renderStrategy === 'lazy') {
            setRenderedSegments(new Set([selectedSegment]));
        } else if (renderStrategy === 'progressive') {
            setRenderedSegments(prev => new Set([...prev, selectedSegment]));
        }
    }, [selectedSegment, renderStrategy, segments]);

    return [renderedSegments, setRenderedSegments] as const;
}

function useProgressiveRendering<Segments extends readonly Segment[]>(
    segments: Segments,
    renderStrategy: RenderStrategy,
    renderedSegments: Set<string>,
    setRenderedSegments: React.Dispatch<React.SetStateAction<Set<string>>>,
    progressiveRenderingDelay: number,
) {
    useEffect(() => {
        if (renderStrategy !== 'progressive') return undefined;

        const segmentKeys = segments.map(({ title, ...rest }) => ('key' in rest ? rest.key : title));
        const unrenderedSegments = segmentKeys.filter(key => !renderedSegments.has(key));

        if (unrenderedSegments.length === 0) return undefined;

        const interval = setInterval(() => {
            setRenderedSegments(prev => {
                const currentSegmentKeys = segments.map(({ title, ...rest }) => ('key' in rest ? rest.key : title));
                const stillUnrendered = currentSegmentKeys.filter(key => !prev.has(key));

                if (stillUnrendered.length === 0) {
                    clearInterval(interval);
                    return prev;
                }

                const nextSegment = stillUnrendered[0];
                return new Set([...prev, nextSegment]);
            });
        }, progressiveRenderingDelay);

        return () => clearInterval(interval);
    }, [segments, renderStrategy, renderedSegments, setRenderedSegments, progressiveRenderingDelay]);
}

/**
 * If your segment objects have a `key` property, that is what will be passed to the `onSegmentChange` callback.
 * Otherwise, the `title` property will be passed.
 * The same logic applies to the `selectedSegment` prop. If your segments have keys selectedSegment should be one of those keys
 * otherwise it should be the `title` of the segment.
 */
export const SegmentedControl = <Segments extends readonly Segment[]>({
    segments,
    classNames,
    onSegmentChange,
    selectedSegment,
    destroyInactiveTabPanel = true,
    renderStrategy = 'progressive',
    progressiveRenderingDelay = 500,
}: Props<Segments>) => {
    const [renderedSegments, setRenderedSegments] = useRenderedSegments(
        segments,
        selectedSegment as string,
        renderStrategy,
    );

    useProgressiveRendering(segments, renderStrategy, renderedSegments, setRenderedSegments, progressiveRenderingDelay);

    return (
        <Tabs
            destroyInactiveTabPanel={destroyInactiveTabPanel}
            variant="underlined"
            color="primary"
            fullWidth
            selectedKey={selectedSegment as string}
            onSelectionChange={key => {
                onSegmentChange?.(key as InferKeyOrTitle<Segments>);
            }}
            classNames={{
                tabWrapper: twMerge([classNames?.tabWrapper]),
                base: twMerge(['overflow-x-clip', classNames?.base]),
                tabList: twMerge([
                    'p-0',
                    'border-b border-solid border-b-slate-grey-200',
                    'overflow-x-visible',
                    classNames?.tabList,
                ]),
                tab: twMerge(['data-[hover-unselected=true]:opacity-100', 'pb-5 h-9', classNames?.tab]),
                tabContent: twMerge(['text-base', 'text-black', 'font-semibold', classNames?.tabContent]),
                cursor: twMerge(['w-full', 'bottom-[-1px]', 'rounded-1', 'shadow-none', classNames?.cursor]),
                panel: twMerge(['p-0', classNames?.panel]),
            }}
        >
            {segments.map(({ title, content, disabled, ...rest }) => {
                const segmentKey = 'key' in rest ? rest.key : title;
                const shouldRender = renderedSegments.has(segmentKey);
                return (
                    <Tab key={segmentKey} title={title} disabled={disabled}>
                        {shouldRender ? content : null}
                    </Tab>
                );
            })}
        </Tabs>
    );
};
